/**
 * Painel da camera: a foto de agora, e o timelapse do cultivo.
 *
 * Responsabilidade: desenhar as imagens que o `store` guarda para o aparelho
 * selecionado, nos dois modos que o segmento no alto escolhe — "Ao vivo" (a
 * foto sob demanda do §7, com o botao que a pede) e "Timelapse" (o acervo
 * horario, com periodo, velocidade e scrubber).
 *
 * Os dois modos dividem a mesma `.camera-box`, o mesmo carregador com decode
 * antecipado e o mesmo carimbo de hora. Sao dois modos de UM painel e nao dois
 * paineis porque o usuario nao quer duas caixas de imagem lado a lado — quer a
 * camera, e escolher o que ver nela. Mesma decisao do painel local
 * (`gfc/firmware/data/index.html`, `#cam-mode-toggle`), incluindo os rotulos.
 *
 * NAO e responsabilidade deste arquivo: buscar a lista. Quem faz o GET e
 * `acoes.acompanharTimelapse()` (timelapse, que depende do periodo) e
 * `acoes.carregarDetalhes()` (snapshots, que nao dependem de nada) — este
 * arquivo so escreve o periodo no `store` (mutacao unica) e desenha o que
 * voltar. Tambem nao decide quando a captura termina: quem apaga o estado de
 * "capturando" e `store.aplicarUpload()`, ao reconhecer a foto pelo ULID.
 * Tambem nao assina URL: isso e `app/services/storage.py`, e o navegador
 * carrega a imagem direto do object storage — a API nunca vira proxy de bytes
 * (contrato §1).
 *
 * ## Offline funciona, e nao e acidente
 *
 * A URL assinada vence (`expires`, 15 min por padrao), entao a que ficou no
 * cache de dados esta morta quando o app abre sem rede. Mesmo assim o frame
 * aparece: o service worker guarda a resposta com a chave `origin + pathname`,
 * SEM a query da assinatura, porque o objeto naquele caminho e imutavel
 * (`timelapse/{tenant}/{device}/{YYYY-MM-DD_HH}.jpg` e escrito uma vez). A
 * requisicao com assinatura vencida da cache hit e nunca chega a rede.
 *
 * Vale igual para o snapshot, e por construcao: o nome dele e o ULID do
 * comando que o pediu (`snapshot/{tenant}/{device}/{ULID}.jpg`, §7), entao cada
 * foto tem caminho proprio e imutavel. Um nome fixo tipo `latest.jpg` teria
 * quebrado exatamente isto — o cache serviria para sempre a primeira foto.
 *
 * Frame que nunca foi visto online nao aparece offline — e ai o painel diz
 * isso, em vez de mostrar imagem quebrada.
 *
 * ## Por que decodificar antes de trocar o `src`
 *
 * Atribuir `src` e esperar o `load` do proprio `<img>` visivel deixa a caixa
 * piscando o quadro anterior enquanto o proximo baixa — a 10 fps, com cada
 * frame vindo do object storage, isso e a reproducao inteira. Aqui cada frame
 * e baixado e DECODIFICADO num `Image` fora da arvore, e so entao vira o `src`
 * do `<img>`; o passo seguinte da reproducao so e agendado depois disso, entao
 * uma conexao lenta atrasa o timelapse em vez de embaralha-lo. Mesma solucao
 * do painel local (`gfc/firmware/data/app.js`), e o motivo de o `<img>` nao
 * precisar mais de `loading="lazy"` nem de um ouvinte de `error`.
 *
 * ## O quadro nao segue o mais novo enquanto esta tocando
 *
 * `colado` e o que faz o painel em repouso mostrar sempre o frame mais recente,
 * inclusive o que chega pelo relay (`WsUpload`) com a tela aberta. Ele e
 * DESLIGADO enquanto se toca: sem isso, o redesenho periodico do `views/app.ts`
 * (a cada 15 s, e a cada quadro de telemetria) reposicionava a reproducao no
 * ultimo frame no meio da passagem — o salto de "15" para "201" que se via.
 */

import * as acoes from "./../acoes";
import * as store from "./../store";
import type { UploadOut } from "./../tipos";
import { el, type Painel } from "./../ui";

type Modo = "vivo" | "timelapse";

/** Velocidades oferecidas, e a que abre selecionada. */
const FPS = [2, 5, 10];
const FPS_PADRAO = 5;

/** Quantos frames a frente ja vao baixando, e quantos decodificados ficam. */
const ADIANTAR = 3;
const TETO_DECODIFICADAS = 12;

interface Opcao {
  valor: string;
  rotulo: string;
}

/** Espelha o seletor do painel local (`gfc/firmware/data/index.html`). */
const PERIODOS: Opcao[] = [
  { valor: "1", rotulo: "24 h" },
  { valor: "7", rotulo: "7 dias" },
  { valor: "30", rotulo: "30 dias" },
  { valor: "", rotulo: "tudo" },
  { valor: "custom", rotulo: "personalizado…" },
];

export function montar(): Painel {
  let modo: Modo = "vivo";
  let indice = 0;
  let colado = true; // seguindo o frame mais novo
  let tocando = false;
  let timer: number | null = null;
  let listaAtual: UploadOut[] = [];
  /** Cresce a cada pedido de quadro: o que voltar atrasado nao pinta por cima. */
  let geracao = 0;
  const decodificadas = new Map<string, HTMLImageElement>();

  // Nasce escondido: o estado de partida deste painel e "sem frame ainda", e
  // quem o revela e o primeiro quadro que termina de decodificar.
  const imagem = el("img", { alt: "Frame do timelapse", hidden: true });
  const vazio = el("p", { class: "hint", text: "Sem frames ainda." });
  const caixa = el("div", { class: "camera-box" }, imagem, vazio);

  const periodo = el("select", { class: "tl-periodo", "aria-label": "Período" });
  for (const opcao of PERIODOS) {
    periodo.append(el("option", { value: opcao.valor, text: opcao.rotulo }));
  }
  periodo.value = "1";

  const de = el("input", { type: "date", "aria-label": "De" });
  const ate = el("input", { type: "date", "aria-label": "Até" });
  const personalizado = el(
    "div",
    { class: "tl-custom", hidden: true },
    de,
    el("span", { class: "hint", text: "até" }),
    ate,
  );

  // Sem a classe `small`: o botao tem tamanho FIXO no CSS, e nao um que sai do
  // conteudo — tocando, o rotulo fica vazio (as duas barras do ⏸ sao
  // desenhadas em CSS) e um botao dimensionado pelo texto encolhia de 29x28
  // para 22x14 px, com as barras transbordando por cima da borda.
  const tocar = el("button", {
    type: "button",
    class: "primary tl-play",
    text: "▶",
    "aria-label": "Tocar",
  });

  const velocidade = el("select", { class: "tl-fps", "aria-label": "Velocidade" });
  for (const fps of FPS) {
    velocidade.append(el("option", { value: String(fps), text: `${fps} fps` }));
  }
  velocidade.value = String(FPS_PADRAO);

  const scrub = el("input", {
    type: "range",
    min: "0",
    max: "0",
    value: "0",
    step: "1",
    class: "tl-scrub",
    "aria-label": "Frame",
  });
  const carimbo = el("span", { class: "hint", text: "—" });
  const contagem = el("span", { class: "hint", text: "" });

  /**
   * Frames que o aparelho pulou e que nunca vao existir na nuvem (§5).
   *
   * Fica no painel do timelapse, e nao numa tela de diagnostico, porque e aqui
   * que a ausencia se manifesta: o usuario ve o filme pular uma hora e nao tem
   * como saber se aquilo e um buraco de gravacao (aparelho desligado) ou dado
   * perdido no caminho. Antes disto, a unica pista existia no log serial.
   */
  const perdidos = el("p", { class: "hint aviso-perda", hidden: true });

  // --- modo ------------------------------------------------------------

  const abaVivo = el("button", {
    type: "button",
    class: "seg on",
    text: "Ao vivo",
    "aria-pressed": "true",
  });
  const abaTimelapse = el("button", {
    type: "button",
    class: "seg",
    text: "Timelapse",
    "aria-pressed": "false",
  });
  const segmento = el("div", { class: "cam-modo", role: "group" }, abaVivo, abaTimelapse);
  const titulo = el("span", { class: "panel-title", text: "Câmera" });

  const capturar = el("button", {
    type: "button",
    class: "primary",
    text: "Capturar agora",
  });
  const acoesVivo = el("div", { class: "cam-vivo" }, capturar);
  const controlesTimelapse = el(
    "div",
    { class: "tl-controls", hidden: true },
    periodo,
    tocar,
    velocidade,
  );

  capturar.addEventListener("click", () => {
    const alvo = store.aparelhoSelecionado();
    if (alvo === null) return;
    void acoes.capturarAgora(alvo.device.id);
  });

  function definirModo(novo: Modo): void {
    if (modo === novo) return;
    modo = novo;
    // Parar a reproducao ao sair do timelapse nao e cortesia: o timer continua
    // pedindo quadros e trocando o `src` da MESMA `<img>` que o modo ao vivo
    // usa, e a foto recem-tirada seria substituida por um frame de outra hora.
    definirTocando(false);
    const vivo = novo === "vivo";
    // O titulo acompanha o modo, igual ao painel local
    // (`gfc/firmware/data/app.js`, `camMode`): "Câmera" no ao vivo, "Timelapse"
    // no acervo. Um titulo fixo faria o segmento parecer um filtro de uma
    // lista, e nao a escolha do que a caixa mostra.
    titulo.textContent = vivo ? "Câmera" : "Timelapse";
    abaVivo.classList.toggle("on", vivo);
    abaTimelapse.classList.toggle("on", !vivo);
    abaVivo.setAttribute("aria-pressed", String(vivo));
    abaTimelapse.setAttribute("aria-pressed", String(!vivo));
    acoesVivo.hidden = !vivo;
    controlesTimelapse.hidden = vivo;
    personalizado.hidden = vivo || periodo.value !== "custom";
    scrub.hidden = vivo;
    // A lista embaixo do painel mudou inteira, entao o cache de comparacao de
    // `mudou()` nao vale mais. Zerar `listaAtual` NAO basta: com as duas listas
    // vazias — nenhum frame e nenhuma foto — `mudou([])` compara vazio com
    // vazio, responde `false`, e o painel fica com o texto do modo anterior
    // ("Sem frames ainda." na aba ao vivo). Por isso a troca de modo forca.
    listaAtual = [];
    colado = true;
    atualizar(true);
  }

  abaVivo.addEventListener("click", () => definirModo("vivo"));
  abaTimelapse.addEventListener("click", () => definirModo("timelapse"));

  // --- periodo ---------------------------------------------------------
  //
  // O seletor NUNCA e desabilitado, nem com a lista vazia: e ele o unico
  // caminho de volta de uma janela sem frame nenhum.

  periodo.addEventListener("change", () => {
    personalizado.hidden = periodo.value !== "custom";
    definirTocando(false);
    if (periodo.value === "custom") {
      if (de.value !== "" || ate.value !== "") mandarDatas();
      return;
    }
    colado = true;
    store.definirPeriodoTimelapse({
      modo: "relativo",
      dias: periodo.value === "" ? null : Number(periodo.value),
    });
  });

  const mandarDatas = (): void => {
    colado = true;
    store.definirPeriodoTimelapse({ modo: "datas", de: de.value, ate: ate.value });
  };
  de.addEventListener("change", mandarDatas);
  ate.addEventListener("change", mandarDatas);

  // --- reproducao ------------------------------------------------------

  scrub.addEventListener("input", () => {
    indice = Number(scrub.value);
    definirTocando(false);
    void mostrar(indice);
  });

  tocar.addEventListener("click", () => {
    if (tocando) {
      definirTocando(false);
      return;
    }
    if (listaAtual.length < 2) return;
    // No fim da lista o botao e "de novo": volta ao primeiro quadro e toca.
    if (indice >= listaAtual.length - 1) {
      indice = 0;
      void mostrar(0);
    }
    definirTocando(true);
  });

  // A velocidade nao tem ouvinte: `tique()` le o `<select>` a cada passo, entao
  // trocar de fps no meio da reproducao vale ja no proximo quadro.

  function definirTocando(ligado: boolean): void {
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
    tocando = ligado && listaAtual.length > 1;
    // Enquanto toca, nao segue o frame mais novo; ao parar, volta a seguir se
    // parou no fim — que e onde a reproducao termina sozinha.
    colado = tocando ? false : indice >= listaAtual.length - 1;
    desenharBotao();
    if (tocando) void tique();
  }

  async function tique(): Promise<void> {
    if (!tocando) return;
    const inicio = performance.now();
    if (indice >= listaAtual.length - 1) {
      definirTocando(false);
      return;
    }
    indice += 1;
    const pronta = await mostrar(indice);
    if (!tocando) return;
    if (pronta === null) {
      // Quadro que nao carregou (offline, ou objeto que sumiu): segue para o
      // proximo sem gastar o intervalo esperando por nada.
      timer = window.setTimeout(() => void tique(), 0);
      return;
    }
    const intervalo = 1000 / Number(velocidade.value);
    timer = window.setTimeout(() => void tique(), Math.max(0, intervalo - (performance.now() - inicio)));
  }

  /**
   * O ⏸ nao usa glifo: em varios navegadores ele vira emoji azul. Ver CSS.
   *
   * Como o rotulo fica VAZIO tocando, o `aria-label` e o unico nome que sobra
   * para quem navega por leitor de tela — e ele acompanha os tres estados.
   */
  function desenharBotao(): void {
    const noFim = listaAtual.length > 0 && indice >= listaAtual.length - 1;
    tocar.classList.toggle("tl-tocando", tocando);
    tocar.textContent = tocando ? "" : noFim ? "↻" : "▶";
    tocar.setAttribute("aria-label", tocando ? "Pausar" : noFim ? "Tocar do início" : "Tocar");
  }

  // --- desenho ---------------------------------------------------------

  function carregar(frame: UploadOut): Promise<HTMLImageElement | null> {
    const pronta = decodificadas.get(frame.object_key);
    if (pronta !== undefined) return Promise.resolve(pronta);
    const img = new Image();
    img.src = frame.url;
    return img
      .decode()
      .then(() => {
        // Guardado por `object_key` e nao por URL: a assinatura muda a cada
        // listagem, o objeto naquele caminho nao.
        decodificadas.set(frame.object_key, img);
        if (decodificadas.size > TETO_DECODIFICADAS) {
          const maisAntiga = decodificadas.keys().next().value;
          if (maisAntiga !== undefined) decodificadas.delete(maisAntiga);
        }
        return img;
      })
      .catch(() => null);
  }

  function adiantar(): void {
    for (let passo = 1; passo <= ADIANTAR; passo += 1) {
      const frame = listaAtual[indice + passo];
      if (frame !== undefined && !decodificadas.has(frame.object_key)) void carregar(frame);
    }
  }

  async function mostrar(alvo: number): Promise<HTMLImageElement | null> {
    const frame = listaAtual[alvo];
    scrub.value = String(alvo);
    desenharBotao();
    if (frame === undefined) {
      imagem.removeAttribute("src");
      imagem.hidden = true;
      vazio.hidden = false;
      if (modo === "vivo") {
        vazio.textContent = "Nenhuma foto ainda. Toque em “Capturar agora”.";
      } else {
        vazio.textContent = periodoAberto()
          ? "Sem frames ainda."
          : "Nenhum frame neste período.";
      }
      carimbo.textContent = "—";
      contagem.textContent = "";
      return null;
    }

    carimbo.textContent = horaDoFrame(frame);
    // No modo ao vivo o contador nao diz nada: sao as ultimas fotos, nao uma
    // posicao dentro de uma sequencia que se percorre.
    contagem.textContent = modo === "vivo" ? "" : `${alvo + 1}/${listaAtual.length}`;

    const marca = (geracao += 1);
    const pronta = await carregar(frame);
    if (marca !== geracao) return pronta; // outro quadro foi pedido no meio

    if (pronta === null) {
      imagem.hidden = true;
      vazio.hidden = false;
      vazio.textContent =
        store.obter().conexao === "ao-vivo"
          ? "Não foi possível carregar este frame."
          : "Este frame não está no cache — só com conexão.";
      return null;
    }
    imagem.src = pronta.src;
    imagem.hidden = false;
    vazio.hidden = true;
    adiantar();
    return pronta;
  }

  /** `true` quando o periodo escolhido nao recorta nada — nem "tudo", nem datas em branco. */
  function periodoAberto(): boolean {
    const escolhido = store.obter().periodoTimelapse;
    return escolhido.modo === "relativo"
      ? escolhido.dias === null
      : escolhido.de === "" && escolhido.ate === "";
  }

  /**
   * A lista mudou de conteudo, e nao so de identidade do array.
   *
   * Sem esta comparacao, o redesenho periodico do `views/app.ts` refaria o
   * caminho inteiro a cada 15 s — recarregando o quadro na tela e brigando com
   * a reproducao em andamento.
   */
  function mudou(nova: UploadOut[]): boolean {
    return (
      nova.length !== listaAtual.length ||
      nova[0]?.object_key !== listaAtual[0]?.object_key ||
      nova[nova.length - 1]?.object_key !== listaAtual[listaAtual.length - 1]?.object_key
    );
  }

  /**
   * O botao de captura, e o texto que explica por que ele esta desligado.
   *
   * Roda a cada redesenho, e nao so na troca de modo: `cam_available` e
   * `online` chegam pelo relay a qualquer momento, e um aparelho que caiu com
   * a tela aberta tem que desabilitar o botao sozinho — clicar para descobrir
   * pelo 503 seria uma ida ao servidor para dizer o que a tela ja sabia.
   */
  function desenharVivo(): void {
    const alvo = store.aparelhoSelecionado();
    if (alvo === null) {
      capturar.disabled = true;
      return;
    }
    const capturando = store.obter().capturando[alvo.device.id] != null;
    // `?? true` e deliberado: firmware anterior a este campo nao o publica, e
    // um aparelho com camera ficaria com o botao morto para sempre. O erro
    // possivel no outro sentido e um ack `ok:false` dizendo "camera
    // indisponivel", que a tela mostra.
    const temCamera = alvo.state?.cam_available ?? true;
    const online = alvo.device.online;

    capturar.disabled = capturando || !temCamera || !online;
    capturar.textContent = capturando ? "Capturando…" : "Capturar agora";
    if (!temCamera) {
      capturar.title = "Este aparelho não tem câmera.";
    } else if (!online) {
      capturar.title = "O aparelho está sem conexão.";
    } else {
      capturar.removeAttribute("title");
    }
  }

  /**
   * `forcar` existe so para a troca de modo, e nao pode virar o padrao: quem
   * chama de fora e o redesenho periodico do `views/app.ts` (a cada 15 s e a
   * cada quadro de telemetria), e refazer o caminho inteiro ali recarregaria o
   * quadro na tela e brigaria com a reproducao em andamento.
   */
  /**
   * O aviso de frame perdido. So no modo Timelapse: no ao vivo ele nao teria a
   * que se referir, e um aviso permanente que nao explica nada do que esta na
   * tela vira ruido que se aprende a ignorar.
   */
  function desenharPerdas(alvo: ReturnType<typeof store.aparelhoSelecionado>): void {
    const n = alvo?.state?.upload?.pulados_total ?? 0;
    if (modo !== "timelapse" || n <= 0) {
      perdidos.hidden = true;
      return;
    }
    perdidos.hidden = false;
    perdidos.textContent =
      n === 1
        ? "1 quadro não chegou à nuvem e não vai chegar — ele continua no cartão do controlador."
        : `${n} quadros não chegaram à nuvem e não vão chegar — eles continuam no cartão do controlador.`;
  }

  function atualizar(forcar = false): void {
    const alvo = store.aparelhoSelecionado();
    if (modo === "vivo") desenharVivo();
    desenharPerdas(alvo);

    const estado = store.obter();
    const lista =
      alvo === null
        ? []
        : ((modo === "vivo" ? estado.snapshots : estado.uploads)[alvo.device.id] ?? []);
    if (!forcar && !mudou(lista)) return;

    const naTela = listaAtual[indice]?.object_key;
    listaAtual = lista;

    scrub.max = String(Math.max(0, lista.length - 1));
    scrub.disabled = lista.length < 2;
    tocar.disabled = lista.length < 2;
    velocidade.disabled = lista.length < 2;

    // Frame novo chegando pelo relay so move a tela quando o painel esta
    // `colado`; fora isso, o que estava sendo visto continua sendo visto — e
    // se ele nao sobreviveu a janela nova, cai no mais recente dela.
    const ondeEstava = naTela === undefined ? -1 : lista.findIndex((u) => u.object_key === naTela);
    indice = colado || ondeEstava < 0 ? Math.max(0, lista.length - 1) : ondeEstava;
    if (lista.length < 2 && tocando) definirTocando(false);
    void mostrar(indice);
  }

  // Nasce em "Ao vivo", entao o que e do timelapse comeca escondido.
  scrub.hidden = true;
  personalizado.hidden = true;

  const raiz = el(
    "section",
    { class: "panel card", id: "timelapse" },
    el(
      "div",
      { class: "panel-head" },
      titulo,
      contagem,
    ),
    segmento,
    caixa,
    acoesVivo,
    controlesTimelapse,
    personalizado,
    scrub,
    el("div", { class: "camera-actions" }, carimbo),
    perdidos,
  );

  return { raiz, atualizar };
}

/**
 * A hora do frame, lida em UTC de proposito.
 *
 * `captured_at` vem do NOME do arquivo (`YYYY-MM-DD_HH`, §7), que o `gfc`
 * escreve com o relogio LOCAL do aparelho e a nuvem le como UTC. Formatar isso
 * no fuso do navegador deslocaria o rotulo em algumas horas — o quadro que a
 * estufa chamou de "14:00" apareceria como 11:00 para quem esta em -03:00, e
 * mudaria de nome se a pessoa viajasse. Em UTC, o rotulo e exatamente o nome
 * que o aparelho deu ao quadro, igual ao do painel local.
 *
 * Fica aqui e nao no `ui.ts` porque depende de saber o que e um frame — que e
 * justamente o que aquele arquivo se proibe de saber.
 */
function horaDoFrame(frame: UploadOut): string {
  const quando = Date.parse(frame.captured_at);
  if (Number.isNaN(quando)) return "—";
  return new Date(quando).toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    // Snapshot em UTC seria ERRADO, e pelo mesmo raciocinio que faz o
    // timelapse ser certo. O `captured_at` de um frame de timelapse e um nome
    // de arquivo escrito com o relogio local do aparelho; ja o de um snapshot
    // sai do ULID que a NUVEM emitiu, e portanto e um instante real em UTC.
    // Mostra-lo em UTC exibiria "17:42" para uma foto tirada as 14:42 em
    // -03:00 — e essa e a unica imagem do painel de que o usuario sabe a hora
    // exata, porque acabou de pedi-la.
    ...(frame.kind === "snapshot" ? {} : { timeZone: "UTC" }),
  });
}
